import { useMemo } from 'react';

import { ViewProps } from './multiple-choice.constants';
import { canShowOptionLetter } from './multiple-choice.utils';
import { MultipleChoiceLetter } from './multiple-choice';
import { MultipleChoiceListText } from './list/multiple-choice-view-list.style';
import { MultipleChoiceItems } from './multiple-choice.style';
import { isSingleSelect } from './logic/is-single-select';
import { getOptionStatus } from './logic/get-option-status';
import type { MultipleChoiceAnswerFragment } from '../../present/activity-answers.graphql.generated';
import MultipleChoiceRadioSelectedAnswer from './list/multiple-choice-radio-selected-answer';
import MultipleChoiceCheckedSelectedAnswer from './list/multiple-choice-checked-selected-answer';

const MultipleChoiceSelectedAnswersList = ({
  id,
  properties,
  selectedAnswers,
  showAnswer,
}: ViewProps & {
  selectedAnswers?: MultipleChoiceAnswerFragment;
  showAnswer: boolean;
}) => {
  const { multipleChoice } = properties;

  const chosenAnswers = useMemo(
    () => selectedAnswers?.multipleChoiceItems || [],
    [selectedAnswers]
  );

  const hasAnswered = useMemo(() => {
    return chosenAnswers.length > 0;
  }, [chosenAnswers]);

  const isOneOptionCorrect = useMemo(
    () =>
      isSingleSelect({
        answerMode: multipleChoice.answerMode,
        isFreeAnswerMultiSelect: multipleChoice.isFreeAnswerMultiSelect,
        options: multipleChoice.options,
      }),
    [multipleChoice]
  );

  return (
    <MultipleChoiceItems role={isOneOptionCorrect ? 'radiogroup' : 'checkbox'}>
      {multipleChoice.options.map((option, index) => {
        const isOptionSelected = !!chosenAnswers.find(
          a => a.itemId === option.itemId && a.isSelected
        );

        const { isCorrect, isIncorrect, isSelected } = getOptionStatus({
          isOptionSelected,
          showAnswer,
          hasAnswered,
          option,
        });

        const content = (
          <>
            {canShowOptionLetter(
              properties.multipleChoice.showOptionLetter
            ) && (
              <MultipleChoiceLetter
                data-selector="multiple-choice-letter"
                index={index}
              />
            )}
            <MultipleChoiceListText>{option.text}</MultipleChoiceListText>
          </>
        );

        return isOneOptionCorrect ? (
          <MultipleChoiceRadioSelectedAnswer
            key={option.itemId}
            id={`${id}-${index}`}
            name={id}
            label={content}
            disabled={hasAnswered && !isOptionSelected}
            isCorrect={showAnswer && isCorrect}
            isIncorrect={showAnswer && isIncorrect}
            isSelected={isSelected}
          />
        ) : (
          <MultipleChoiceCheckedSelectedAnswer
            key={option.itemId}
            label={content}
            disabled={hasAnswered && !isOptionSelected}
            isCorrect={showAnswer && isCorrect}
            isIncorrect={showAnswer && isIncorrect}
            isSelected={isSelected}
          />
        );
      })}
    </MultipleChoiceItems>
  );
};

export default MultipleChoiceSelectedAnswersList;
